import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext.tsx';
import type { FiltersContextProviderProps } from '@openx/components-filters/lib/context/types.ts';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { promptModes } from './constants.ts';
import { useDealPrompt } from './hooks/useDealPrompt.ts';
import { useDealPromptFiltersConfig } from './hooks/useDealPromptFiltersConfig.ts';
import { getAudienceDefaultFilters, useDealPromptMethods } from './hooks/useDealPromptMethods.ts';
import { useDealPromptPermission } from './hooks/useDealPromptPermission.ts';
import { DealPromptContextTypeSelector } from './parts/contextType/DealPromptContextTypeSelector.tsx';
import { DealPromptErrorAlert } from './parts/DealPromptErrorAlert.tsx';
import { DealPromptNoResultsView } from './parts/DealPromptNoResultsView.tsx';
import { DealPromptPromptInput } from './parts/DealPromptPromptInput/DealPromptPromptInput.tsx';
import { DealPromptResults } from './parts/DealPromptResults.tsx';
import { NewSearchButton } from './parts/NewSearchButton.tsx';
import { useDealPromptDomainsContext } from './utils/context/DealPromptDomainsContext.tsx';
import { getSegmentIdsFromSet } from './utils/getTilesData.ts';
import { type DealPromptFilters, dealPromptContextTypes } from './utils/types.ts';

export const DealPrompt = () => {
  const { t } = useTranslation();
  const { state, setHasDifferentFiltersThanWissp } = useDealPrompt();
  const {
    prompt,
    contextType,
    mode,
    lastSubmittedPrompt,
    lastSubmittedContextType,
    loading,
    firstLoadPerformed,
    requestAborted,
    hasError,
  } = state;

  const abortControllerRef = useRef<AbortController | null>(null);
  const { isInternalUser } = useDealPromptPermission();

  const { audienceSegmentsSets, numberOfResults, useDealPromptFilters } = useDealPromptDomainsContext();

  const { criteria, handleFiltersChange } = useDealPromptFilters();

  const { filtersConfig, loadingFilters, visibleFilters } = useDealPromptFiltersConfig({
    contextType,
    filters: criteria.filters,
  });

  const { handleSubmit, handleCancel, setPrompt, setContextType, resetPrompt } = useDealPromptMethods({
    abortControllerRef,
    criteria,
    handleFiltersChange,
  });

  const hasNoResults =
    contextType === dealPromptContextTypes.AUDIENCE
      ? (audienceSegmentsSets?.every(set => getSegmentIdsFromSet(set).length === 0) ?? false)
      : numberOfResults === 0;
  const shouldShowResults =
    !hasError && ((firstLoadPerformed && !requestAborted && !hasNoResults) || (firstLoadPerformed && loading));
  const shouldShowNoResults = !hasError && firstLoadPerformed && !requestAborted && hasNoResults && !loading;

  const isDirty =
    mode === promptModes.EDIT_MODE && (prompt !== lastSubmittedPrompt || contextType !== lastSubmittedContextType);

  const handleOnFiltersChange = useCallback(
    (filters: DealPromptFilters) => {
      const regionChanged = filters.region !== criteria.filters?.region;
      handleFiltersChange(regionChanged ? { ...filters, account: undefined, country: [] } : filters);
      setHasDifferentFiltersThanWissp(false);
    },
    [criteria.filters?.region, handleFiltersChange, setHasDifferentFiltersThanWissp]
  );

  const filtersProviderConfig: FiltersContextProviderProps<DealPromptFilters>['config'] = {
    filtersConfig,
    visibleFilters,
    disabledFilters: [],
    loadingFilters,
    criteria,
    defaultFilters: contextType === dealPromptContextTypes.AUDIENCE ? getAudienceDefaultFilters(isInternalUser) : {},
    onFiltersChange: handleOnFiltersChange,
  };

  return (
    <FiltersContextProvider config={filtersProviderConfig}>
      <Stack
        sx={{
          px: { xs: 2, sm: 4 },
          pb: { xs: 2, sm: 4 },
          boxSizing: 'border-box',
          alignItems: 'center',
          position: 'relative',
        }}
        direction="column"
        spacing={4}
      >
        <Collapse in={mode !== promptModes.PREVIEW_MODE} sx={{ width: '100%' }}>
          <Stack spacing={1.5} sx={{ pt: { xs: 2, sm: 4 }, alignItems: 'center' }}>
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
              <Box
                sx={{
                  height: 28,
                  width: 28,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AutoAwesomeOutlinedIcon sx={theme => ({ fill: theme.palette.charts.orchid })} />
              </Box>

              <Typography variant="h2" sx={{ color: 'text.primary' }}>
                {t('AI-driven discovery for advertising success')}
              </Typography>
            </Stack>

            <Typography sx={{ color: 'text.secondary' }}>
              {t('Describe your goals and instantly get a list of opportunities')}
            </Typography>
          </Stack>
        </Collapse>

        <NewSearchButton
          loading={loading}
          mode={mode}
          hasError={hasError}
          allowDuringError={contextType === dealPromptContextTypes.AUDIENCE}
          onClick={resetPrompt}
        />

        <Stack direction="column" spacing={1} sx={{ width: '100%', maxWidth: 906 }}>
          <DealPromptContextTypeSelector onChange={setContextType} />

          <DealPromptPromptInput
            value={prompt}
            onChange={setPrompt}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            disabled={loading}
            loading={loading}
            mode={mode}
            isDirty={isDirty}
            hasNoResults={hasNoResults}
          />
        </Stack>
      </Stack>

      {shouldShowResults && (
        <Stack direction="column" sx={{ flexGrow: 1 }}>
          <DealPromptResults />
        </Stack>
      )}

      {shouldShowNoResults && <DealPromptNoResultsView />}

      {hasError && <DealPromptErrorAlert onRetry={handleSubmit} />}
    </FiltersContextProvider>
  );
};
